import React, { useEffect, useRef, useState } from "react";
import api, { API, formatApiError } from "@/lib/api";
import { PageContainer, PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import {
  Download, Upload, FileSpreadsheet, FileText, CheckCircle2, AlertTriangle,
  Package, Users, Truck,
} from "lucide-react";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

// ---- Entity configuration ----
const ENTITIES = {
  products: {
    label: "Productos",
    icon: Package,
    base: "/migration/products",
    templateFile: "plantilla_productos.csv",
    description: "Inventario completo de la ferretería: SKU, precio, costo, existencia y más.",
    uniqueLabel: "SKU",
  },
  customers: {
    label: "Clientes",
    icon: Users,
    base: "/migration/customers",
    templateFile: "plantilla_clientes.csv",
    description: "Base de clientes con RNC/cédula, contacto y dirección. Permite emitir Crédito Fiscal.",
    uniqueLabel: "RNC/Cédula",
  },
  suppliers: {
    label: "Suplidores",
    icon: Truck,
    base: "/migration/suppliers",
    templateFile: "plantilla_suplidores.csv",
    description: "Proveedores que abastecen la ferretería con sus datos fiscales.",
    uniqueLabel: "RNC",
  },
};

function downloadBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = filename;
  document.body.appendChild(a); a.click(); a.remove();
  URL.revokeObjectURL(url);
}

async function fetchDownload(path, fallback) {
  const r = await fetch(`${API}${path}`, { credentials: "include" });
  if (!r.ok) throw new Error(`Error ${r.status}`);
  const cd = r.headers.get("content-disposition") || "";
  const m = cd.match(/filename="([^"]+)"/);
  downloadBlob(await r.blob(), m ? m[1] : fallback);
}

function Spec({ spec, entity }) {
  if (!spec) return null;
  return (
    <div className="bg-card border border-border rounded-md overflow-x-auto" data-testid={`${entity}-spec-table`}>
      <table className="w-full text-sm">
        <thead className="overline border-b border-border">
          <tr>
            <th className="text-left p-3">Campo</th>
            <th className="text-center p-3 w-24">Obligatorio</th>
            <th className="text-center p-3 w-20">Único</th>
            <th className="text-left p-3">Tipo</th>
            <th className="text-left p-3">Ejemplo</th>
            <th className="text-left p-3">Descripción</th>
          </tr>
        </thead>
        <tbody>
          {spec.fields.map((f) => (
            <tr key={f.field} className="border-b border-border/60 hover:bg-accent">
              <td className="p-3 data-cell font-bold text-primary">{f.field}</td>
              <td className="p-3 text-center">
                {f.required
                  ? <span className="text-xs px-2 py-0.5 rounded bg-destructive/10 text-destructive font-bold">Sí</span>
                  : <span className="text-xs text-muted-foreground">No</span>}
              </td>
              <td className="p-3 text-center">
                {f.unique
                  ? <span className="text-xs px-2 py-0.5 rounded bg-primary/10 text-primary font-bold">Sí</span>
                  : <span className="text-xs text-muted-foreground">No</span>}
              </td>
              <td className="p-3 data-cell text-xs">{f.type}</td>
              <td className="p-3 data-cell text-xs text-muted-foreground">{f.example}</td>
              <td className="p-3 text-xs">{f.hint}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <div className="p-4 border-t border-border bg-muted/30 text-xs space-y-1">
        <div><strong>Encabezados del CSV (orden):</strong> <code className="font-mono">{spec.headers.join(", ")}</code></div>
        <div><strong>Codificación:</strong> UTF-8 (BOM aceptado). Separador: coma (<code>,</code>).</div>
      </div>
    </div>
  );
}

function ImportTab({ entity }) {
  const cfg = ENTITIES[entity];
  const fileRef = useRef(null);
  const [file, setFile] = useState(null);
  const [mode, setMode] = useState("upsert");
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  // Reset state when entity changes
  useEffect(() => {
    setFile(null); setResult(null); setMode("upsert");
    if (fileRef.current) fileRef.current.value = "";
  }, [entity]);

  const upload = async () => {
    if (!file) return toast.error("Selecciona un archivo CSV");
    setSubmitting(true);
    setResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("mode", mode);
      const { data } = await api.post(`${cfg.base}/import`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setResult(data);
      toast.success(`Importación completa: ${data.created} nuevos, ${data.updated} actualizados`);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSubmitting(false); }
  };

  return (
    <div className="space-y-4">
      <div className="bg-card border border-border rounded-md p-6">
        <div className="overline mb-1">Paso 1</div>
        <h3 className="text-xl font-bold tracking-tight mb-1">Descarga la plantilla</h3>
        <p className="text-sm text-muted-foreground mb-4">
          Plantilla con encabezados correctos y filas de ejemplo. Edita en Excel / Google Sheets y guarda como CSV.
        </p>
        <Button
          variant="outline"
          onClick={async () => {
            try { await fetchDownload(`${cfg.base}/template`, cfg.templateFile); }
            catch (e) { toast.error("Error descargando plantilla"); }
          }}
          data-testid={`download-template-${entity}`}
        >
          <FileSpreadsheet className="w-4 h-4 mr-2" /> Descargar plantilla CSV
        </Button>
      </div>

      <div className="bg-card border border-border rounded-md p-6">
        <div className="overline mb-1">Paso 2</div>
        <h3 className="text-xl font-bold tracking-tight mb-4">Sube tu archivo</h3>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
          <div className="md:col-span-2">
            <label className="overline mb-1.5 block">Archivo CSV</label>
            <input
              ref={fileRef}
              type="file"
              accept=".csv"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
              className="block w-full text-sm border border-border rounded-md p-2 bg-background file:mr-3 file:px-3 file:py-1 file:rounded file:border-0 file:bg-primary file:text-primary-foreground file:font-bold file:text-xs file:cursor-pointer"
              data-testid={`import-file-${entity}`}
            />
          </div>
          <div>
            <label className="overline mb-1.5 block">Modo</label>
            <Select value={mode} onValueChange={setMode}>
              <SelectTrigger data-testid={`import-mode-${entity}`}><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="upsert">Actualizar si el {cfg.uniqueLabel} existe</SelectItem>
                <SelectItem value="skip_existing">Omitir si el {cfg.uniqueLabel} existe</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <Button
          onClick={upload}
          disabled={!file || submitting}
          className="mt-4 bg-primary font-bold"
          data-testid={`import-submit-${entity}`}
        >
          <Upload className="w-4 h-4 mr-2" /> {submitting ? "Procesando…" : `Importar ${cfg.label.toLowerCase()}`}
        </Button>
      </div>

      {result && (
        <div className="bg-card border border-border rounded-md p-6" data-testid={`import-result-${entity}`}>
          <h3 className="text-xl font-bold tracking-tight mb-4 flex items-center gap-2">
            {result.errors_count === 0
              ? <><CheckCircle2 className="w-5 h-5 text-success" /> Importación exitosa</>
              : <><AlertTriangle className="w-5 h-5 text-amber-600" /> Importación con observaciones</>}
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div className="kpi-card"><div className="overline">Creados</div><div className="text-2xl font-black mt-2 data-cell text-success">{result.created}</div></div>
            <div className="kpi-card"><div className="overline">Actualizados</div><div className="text-2xl font-black mt-2 data-cell text-info">{result.updated}</div></div>
            <div className="kpi-card"><div className="overline">Omitidos</div><div className="text-2xl font-black mt-2 data-cell">{result.skipped}</div></div>
            <div className="kpi-card border-destructive/40"><div className="overline text-destructive">Errores</div><div className="text-2xl font-black mt-2 data-cell text-destructive">{result.errors_count}</div></div>
          </div>
          {result.errors && result.errors.length > 0 && (
            <div className="mt-4 border border-destructive/30 rounded-md max-h-[260px] overflow-y-auto" data-testid={`import-errors-${entity}`}>
              <table className="w-full text-xs">
                <thead className="overline border-b border-border bg-destructive/5">
                  <tr><th className="text-left p-2 w-20">Fila</th><th className="text-left p-2">Error</th></tr>
                </thead>
                <tbody>
                  {result.errors.map((e, i) => (
                    <tr key={i} className="border-b border-border/40">
                      <td className="p-2 data-cell">{e.row}</td>
                      <td className="p-2">{e.error}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function ExportTab({ entity }) {
  const cfg = ENTITIES[entity];
  const onExport = async () => {
    try {
      await fetchDownload(`${cfg.base}/export`, `${entity}_export.csv`);
      toast.success(`${cfg.label} exportados`);
    } catch (e) { toast.error(e.message); }
  };
  return (
    <div className="bg-card border border-border rounded-md p-6">
      <div className="overline mb-1">Exportar</div>
      <h3 className="text-xl font-bold tracking-tight mb-1">{cfg.label} en CSV</h3>
      <p className="text-sm text-muted-foreground mb-4">
        Descarga todos los {cfg.label.toLowerCase()} en formato CSV (mismo formato que la plantilla). Útil para
        respaldo, ajuste masivo en Excel o migración a otro sistema.
      </p>
      <Button onClick={onExport} className="bg-primary font-bold" data-testid={`export-${entity}`}>
        <Download className="w-4 h-4 mr-2" /> Exportar {cfg.label.toLowerCase()}
      </Button>
    </div>
  );
}

function EntityPanel({ entity }) {
  const cfg = ENTITIES[entity];
  const [spec, setSpec] = useState(null);
  useEffect(() => {
    setSpec(null);
    api.get(`${cfg.base}/spec`).then(({ data }) => setSpec(data)).catch(() => setSpec(null));
  }, [entity, cfg.base]);

  return (
    <>
      <div className="bg-muted/40 border border-border rounded-md p-4 mb-4 flex gap-3 items-start text-sm">
        <cfg.icon className="w-5 h-5 text-primary mt-0.5 shrink-0" />
        <div>
          <b>{cfg.label}.</b> {cfg.description}
        </div>
      </div>
      <Tabs defaultValue="import">
        <TabsList className="mb-4">
          <TabsTrigger value="import" data-testid={`tab-import-${entity}`}><Upload className="w-3.5 h-3.5 mr-1.5" /> Importar</TabsTrigger>
          <TabsTrigger value="export" data-testid={`tab-export-${entity}`}><Download className="w-3.5 h-3.5 mr-1.5" /> Exportar</TabsTrigger>
          <TabsTrigger value="spec" data-testid={`tab-spec-${entity}`}><FileText className="w-3.5 h-3.5 mr-1.5" /> Especificación</TabsTrigger>
        </TabsList>
        <TabsContent value="import"><ImportTab entity={entity} /></TabsContent>
        <TabsContent value="export"><ExportTab entity={entity} /></TabsContent>
        <TabsContent value="spec"><Spec spec={spec} entity={entity} /></TabsContent>
      </Tabs>
    </>
  );
}

export function MigrationContent() {
  return (
    <Tabs defaultValue="products">
      <TabsList className="mb-4">
        <TabsTrigger value="products" data-testid="entity-products"><Package className="w-3.5 h-3.5 mr-1.5" /> Productos</TabsTrigger>
        <TabsTrigger value="customers" data-testid="entity-customers"><Users className="w-3.5 h-3.5 mr-1.5" /> Clientes</TabsTrigger>
        <TabsTrigger value="suppliers" data-testid="entity-suppliers"><Truck className="w-3.5 h-3.5 mr-1.5" /> Suplidores</TabsTrigger>
      </TabsList>
      <TabsContent value="products"><EntityPanel entity="products" /></TabsContent>
      <TabsContent value="customers"><EntityPanel entity="customers" /></TabsContent>
      <TabsContent value="suppliers"><EntityPanel entity="suppliers" /></TabsContent>
    </Tabs>
  );
}

export default function Migration() {
  return (
    <PageContainer>
      <PageHeader
        title="Migración de datos"
        subtitle="Importa y exporta productos, clientes y suplidores en formato CSV. Útil para carga inicial, ajustes masivos y respaldo."
        testid="migration-header"
      />
      <MigrationContent />
    </PageContainer>
  );
}
